import React, { useCallback, useEffect, useMemo, useState } from "react";
import { userAuthHeaders } from "./userAuth";

/**
 * 후보 세번 비교(2026-10-06) — 같은 한국 물품의 서로 다른 HSK 10자리 후보 둘을 나란히 본다(검증 화면 연동 지시문).
 * 기본 조회(세번 품명·필수·배제 조건)는 들어오면 바로, 비용·시간이 드는 '물품 기준으로 비교 검토'는 사용자가 누를 때만.
 * 판정이 아니다 — 시스템 검토 의견과 사용자의 '채택 후보'를 나누고, 채택도 신고서 수정이 아니다. 세율로 우열을 정하지 않는다.
 */

const MUTED = "var(--audit-muted, #6b7482)";
const LINE = "var(--audit-line, #e3e6ea)";
const BLUE = "#1a56db";

export function formatHsk(code) {
  const d = String(code || "").replace(/\D/g, "");
  return d.length === 10 ? `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}` : d;
}

/** 검토 요약(복사용) — 물품·후보 둘·확인 범위/기준일·주요 근거·미확인 사항·채택 후보. */
export function summaryText({ payload, review, adopted, roleLabel }) {
  const p = payload?.product || {};
  const lines = ["[후보 세번 비교 검토]"];
  const desc = [p.trade_name, [p.spec1, p.spec2, p.spec3].filter(Boolean).join(" "), (p.components || []).join(", ")].filter(Boolean).join(" / ");
  lines.push(`물품: ${desc || "-"}`);
  if (p.supplement) lines.push(`사용자 보충: ${p.supplement}`);
  (payload?.candidates || []).forEach((c) => {
    lines.push(`${roleLabel(c.role)}: ${formatHsk(c.hsk)}${c.reference_date ? ` (기준일 ${c.reference_date})` : ""}`);
  });
  lines.push("자료 기준: 현재(2026) 관세율표·요건 자료");
  if (review?.status === "ok") {
    lines.push(`확인 범위: ${{ hs4: "호(4자리)까지", hs6: "소호(6자리)까지", hs10: "국내 10자리까지" }[review.scope] || review.scope}`);
    (review.key_differences || []).forEach((d) => lines.push(`- 가르는 조건: ${d.condition} (물품: ${d.product || "모름"})`));
    (review.candidates || []).forEach((c) => {
      lines.push(`${formatHsk(c.hsk)}: ${c.verdict}${c.basis ? ` — ${c.basis}` : ""}`);
      if (c.unknown?.length) lines.push(`  미확인: ${c.unknown.join(", ")}`);
    });
    if (review.questions?.length) lines.push(`추가 확인: ${review.questions.join(" / ")}`);
    if (review.opinion?.hsk) lines.push(`시스템 검토 의견(확정 아님): ${formatHsk(review.opinion.hsk)} — ${review.opinion.why || ""}`);
  } else {
    lines.push("물품 기준 비교 검토: 실행 안 함");
  }
  lines.push(`채택 후보(사용자 선택, 신고서 수정 아님): ${adopted ? formatHsk(adopted) : "선택 안 함"}`);
  return lines.join("\n");
}

const VERDICT_STYLE = {
  "조건 충족": { color: BLUE, border: "#b9cdf5" },
  "조건 충돌": { color: "#1f2937", border: "#9ca3af" },
  "추가 확인": { color: MUTED, border: LINE },
  "근거 부족": { color: MUTED, border: LINE },
};

function useCandidateBasics(codes) {
  const [data, setData] = useState({});
  useEffect(() => {
    let alive = true;
    codes.forEach((hsk) => {
      if (!hsk || data[hsk]) return;
      Promise.all([
        fetch(`/api/onto/hsk-detail?hsk=${hsk}&lang=kor`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        fetch("/api/onto/classify/preview/target-conditions", {
          method: "POST",
          headers: { ...userAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ code: hsk }),
        }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]).then(([detail, cond]) => { if (alive) setData((s) => ({ ...s, [hsk]: { detail, cond } })); });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes.join(",")]);
  return data;
}

export default function CandidateHskCompare({ comparisonId = null }) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [manual, setManual] = useState({ a: "", b: "", trade_name: "", spec: "" });
  const [supplement, setSupplement] = useState("");
  const [showProduct, setShowProduct] = useState(true);
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [precise, setPrecise] = useState(false);
  const [adopted, setAdopted] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!comparisonId) return;
    setError(null);
    fetch(`/api/onto/hs-compare/sessions/${encodeURIComponent(comparisonId)}`, { headers: userAuthHeaders() })
      .then(async (r) => {
        if (r.status === 401) throw new Error("login");
        const j = await r.json().catch(() => ({}));
        if (r.status === 410) throw new Error(j.message || "비교 화면이 만료됐습니다. 검증 화면에서 다시 열어 주세요.");
        if (!r.ok) throw new Error(j.message || `오류 ${r.status}`);
        return j;
      })
      .then((j) => { setPayload(j); setSupplement(j.product?.supplement || ""); setReview(null); setAdopted(""); })
      .catch((e) => setError(e.message === "login" ? "로그인이 필요합니다. 로그인하면 이 비교 화면으로 돌아옵니다." : e.message));
  }, [comparisonId]);

  const codes = useMemo(() => (payload?.candidates || []).map((c) => c.hsk), [payload]);
  const basics = useCandidateBasics(codes);
  const fromVerify = payload?.source === "import_verify" || (payload?.candidates || []).some((c) => c.role === "current");
  const roleLabel = useCallback((role) => (role === "current" ? "현재 신고 세번" : role === "comparison" ? "비교 이력 세번" : role === "a" ? "후보 A" : "후보 B"), []);

  const startManual = (e) => {
    e.preventDefault();
    const a = manual.a.replace(/\D/g, "");
    const b = manual.b.replace(/\D/g, "");
    if (a.length !== 10 || b.length !== 10) { setError("후보 세번은 10자리 숫자로 넣어 주세요."); return; }
    if (a === b) { setError("서로 다른 두 세번을 넣어 주세요."); return; }
    setError(null);
    setReview(null);
    setAdopted("");
    setPayload({ source: "manual", product: { trade_name: manual.trade_name, spec1: manual.spec, spec2: "", spec3: "", components: [], supplement: "" },
      candidates: [{ role: "a", hsk: a }, { role: "b", hsk: b }] });
  };

  const runReview = async () => {
    if (!payload) return;
    setReviewing(true);
    setReview(null);
    try {
      const r = await fetch("/api/onto/hs-compare/review", {
        method: "POST",
        headers: { ...userAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ product: { ...payload.product, supplement }, candidates: codes, precise }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 402) setReview({ status: "subscription", message: j.message });
      else if (!r.ok) setReview({ status: "error", message: j.message || `오류 ${r.status}` });
      else setReview(j);
    } catch (e) {
      setReview({ status: "error", message: String(e?.message || e) });
    } finally {
      setReviewing(false);
    }
  };

  const copySummary = async () => {
    const t = summaryText({ payload: { ...payload, product: { ...payload.product, supplement } }, review, adopted, roleLabel });
    try { await navigator.clipboard.writeText(t); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) { window.prompt("검토 요약", t); }
  };

  const cell = { padding: "8px 10px", borderBottom: `1px solid ${LINE}`, verticalAlign: "top", fontSize: 13, lineHeight: 1.6 };
  const head = { ...cell, color: MUTED, fontWeight: 500, width: 140, background: "var(--audit-subtle, #f8f9fa)" };
  const p = payload?.product || {};

  return (
    <div data-testid="candidate-hsk-compare" style={{ display: "grid", gap: 14, fontSize: 13, color: "#1f2937" }}>
      {!payload && !comparisonId && (
        <form onSubmit={startManual} style={{ display: "grid", gap: 8, border: `1px solid ${LINE}`, borderRadius: 8, padding: 12 }}>
          <b>같은 물품의 두 후보 세번 비교</b>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input aria-label="후보 A" placeholder="후보 A 10자리 (예: 3926.30-0000)" value={manual.a} onChange={(e) => setManual({ ...manual, a: e.target.value })} style={inputStyle(220)} />
            <input aria-label="후보 B" placeholder="후보 B 10자리 (예: 8708.99-9000)" value={manual.b} onChange={(e) => setManual({ ...manual, b: e.target.value })} style={inputStyle(220)} />
          </div>
          <input aria-label="거래품명" placeholder="거래품명" value={manual.trade_name} onChange={(e) => setManual({ ...manual, trade_name: e.target.value })} style={inputStyle("100%")} />
          <input aria-label="규격" placeholder="규격·성분" value={manual.spec} onChange={(e) => setManual({ ...manual, spec: e.target.value })} style={inputStyle("100%")} />
          <div><button type="submit" style={btn(true)}>비교</button></div>
        </form>
      )}
      {error && <p role="alert" style={{ margin: 0 }}>{error}</p>}
      {payload && (
        <>
          <section style={{ border: `1px solid ${LINE}`, borderRadius: 8, padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
              <b>물품 설명{fromVerify ? " (검증 화면에서 전달)" : ""}</b>
              <button type="button" onClick={() => setShowProduct((v) => !v)} style={linkBtn}>{showProduct ? "접기" : "펼치기"}</button>
            </div>
            {showProduct && (
              <div style={{ marginTop: 6, display: "grid", gap: 2 }}>
                <div>거래품명: {p.trade_name || <span style={{ color: MUTED }}>없음</span>}</div>
                <div>규격: {[p.spec1, p.spec2, p.spec3].filter(Boolean).join(" ") || <span style={{ color: MUTED }}>없음</span>}</div>
                {p.components?.length > 0 && <div>성분: {p.components.join(", ")}</div>}
                {(payload.candidates || []).filter((c) => c.product_observation).map((c) => (
                  <div key={c.hsk} style={{ color: MUTED }}>비교 이력 설명({formatHsk(c.hsk)}): {Object.values(c.product_observation).join(" ")}</div>
                ))}
                <label style={{ display: "grid", gap: 4, marginTop: 6 }}>
                  <span style={{ color: MUTED }}>보충 설명(재질·용도·구성·작동 방식 — 사용자가 입력한 정보로 표시됩니다)</span>
                  <textarea aria-label="보충 설명" value={supplement} onChange={(e) => setSupplement(e.target.value)} rows={2} style={{ ...inputStyle("100%"), resize: "vertical" }} />
                </label>
              </div>
            )}
            <div style={{ marginTop: 6, color: MUTED, fontSize: 12 }}>
              자료 기준: 현재(2026) 관세율표·요건 자료로 비교합니다
              {(payload.candidates || []).some((c) => c.reference_date && c.reference_date < "2026-01-01") ? " · 과거 신고일의 적정성까지 확인한 것은 아닙니다" : ""}
            </div>
          </section>

          <table data-testid="candidate-compare-table" style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
            <thead>
              <tr>
                <th style={{ ...head, borderTop: `1px solid ${LINE}` }} />
                {(payload.candidates || []).map((c) => (
                  <th key={c.hsk} style={{ ...cell, borderTop: `1px solid ${LINE}`, textAlign: "left" }}>
                    <div style={{ color: MUTED, fontWeight: 500 }}>{roleLabel(c.role)}</div>
                    <div style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 700 }}>{formatHsk(c.hsk)}</div>
                    <a href={`/?audit=onto-detail&hs=${c.hsk.slice(0, 4)}&hsk=${c.hsk}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 400 }}>세번 상세 ↗</a>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row label="품명" head={head} cell={cell} codes={codes} render={(h) => basics[h]?.detail?.name_ko || basics[h]?.detail?.popum || <span style={{ color: MUTED }}>불러오는 중…</span>} />
              <Row label="상위 경로" head={head} cell={cell} codes={codes} render={(h) => {
                const c = basics[h]?.cond;
                if (!c) return <span style={{ color: MUTED }}>…</span>;
                return `제${c.chapter || h.slice(0, 2)}류 · ${h.slice(0, 4)}호 · ${h.slice(0, 4)}.${h.slice(4, 6)}소호 · 국내 ${formatHsk(h)}`;
              }} />
              <Row label="포함 요건" head={head} cell={cell} codes={codes} render={(h) => <ConditionList cond={basics[h]?.cond} kind="required" />} />
              <Row label="제외 요건" head={head} cell={cell} codes={codes} render={(h) => <ConditionList cond={basics[h]?.cond} kind="exclusions" />} />
              <Row label="근거 자료" head={head} cell={cell} codes={codes} render={(h) => (
                <span style={{ display: "grid", gap: 2 }}>
                  <a href={`/?audit=onto-detail&hs=${h.slice(0, 4)}&hsk=${h}`} target="_blank" rel="noreferrer">주·해설·분류사례 ↗</a>
                </span>
              )} />
              <Row label="신고 자료 참고값" head={head} cell={cell} codes={codes} render={(h) => {
                const c = (payload.candidates || []).find((x) => x.hsk === h);
                const rr = c?.reported_rate;
                if (!rr) return <span style={{ color: MUTED }}>없음</span>;
                const pct = rr.percent === null || rr.percent === undefined ? "수집 안 됨" : `${rr.percent}%`;
                return <span>구분 {rr.category || "수집 안 됨"} · 실행세율 {pct} <span style={{ color: MUTED }}>(참고값 — 세율로 분류를 정하지 않습니다)</span></span>;
              }} />
              {review?.status === "ok" && (
                <Row label="물품 기준 검토" head={head} cell={cell} codes={codes} render={(h) => {
                  const c = (review.candidates || []).find((x) => x.hsk === h);
                  if (!c) return null;
                  const st = VERDICT_STYLE[c.verdict] || VERDICT_STYLE["근거 부족"];
                  return (
                    <span style={{ display: "grid", gap: 4 }}>
                      <span><b style={{ color: st.color, border: `1px solid ${st.border}`, borderRadius: 4, padding: "0 6px" }}>{c.verdict}</b></span>
                      {c.basis && <span>{c.basis}</span>}
                      {c.confirmed?.length > 0 && <span>확인된 사실: {c.confirmed.join(" · ")}</span>}
                      {c.conflicts?.length > 0 && <span>충돌한 사실: {c.conflicts.join(" · ")}</span>}
                      {c.unknown?.length > 0 && <span style={{ color: MUTED }}>아직 모르는 사실: {c.unknown.join(" · ")}</span>}
                    </span>
                  );
                }} />
              )}
              <Row label="채택 후보" head={head} cell={cell} codes={codes} render={(h) => (
                <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input type="radio" name="adopted" aria-label={`채택 ${h}`} checked={adopted === h} onChange={() => setAdopted(h)} />
                  이 후보 채택(사용자 선택 · 신고서 수정 아님)
                </label>
              )} />
            </tbody>
          </table>

          <section style={{ border: `1px solid ${LINE}`, borderRadius: 8, padding: 12, display: "grid", gap: 8 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={runReview} disabled={reviewing} style={btn(true)}>{reviewing ? "검토 중…" : "물품 기준으로 비교 검토"}</button>
              <label style={{ display: "flex", gap: 4, alignItems: "center", color: MUTED }}>
                <input type="checkbox" checked={precise} onChange={(e) => setPrecise(e.target.checked)} /> 정밀(느림, 30초 안팎)
              </label>
              <span style={{ color: MUTED, fontSize: 12 }}>AI 가 물품 설명과 두 후보의 요건을 견줘 검토 의견을 냅니다 · 위 기본 조회와 달리 비용·시간이 듭니다 · 확정 아님</span>
            </div>
            {review?.status === "subscription" && <p role="status" style={{ margin: 0 }}>{review.message}</p>}
            {review?.status === "error" && <p role="alert" style={{ margin: 0 }}>{review.message}</p>}
            {review?.status === "unavailable" && <p role="status" style={{ margin: 0 }}>지금은 검토할 수 없습니다. 잠시 뒤 다시 눌러 주세요.</p>}
            {review?.status === "ok" && (
              <div data-testid="candidate-review" style={{ display: "grid", gap: 8 }}>
                <div><b>분류상 핵심 차이</b>
                  {(review.key_differences || []).length ? (
                    <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 4 }}>
                      <thead><tr>
                        <th style={{ ...cell, textAlign: "left", color: MUTED }}>가르는 조건</th>
                        <th style={{ ...cell, textAlign: "left", color: MUTED }}>{formatHsk(codes[0])}</th>
                        <th style={{ ...cell, textAlign: "left", color: MUTED }}>{formatHsk(codes[1])}</th>
                        <th style={{ ...cell, textAlign: "left", color: MUTED }}>이 물품</th>
                      </tr></thead>
                      <tbody>{review.key_differences.map((d, i) => (
                        <tr key={i}><td style={cell}>{d.condition}</td><td style={cell}>{d.a}</td><td style={cell}>{d.b}</td>
                          <td style={{ ...cell, color: d.product && d.product !== "모름" ? "#1f2937" : MUTED }}>{d.product || "모름"}</td></tr>
                      ))}</tbody>
                    </table>
                  ) : <div style={{ color: MUTED }}>없음</div>}
                </div>
                <div><b>현재 정보로 판단 가능한 범위</b> · {{ hs4: "호(4자리)까지", hs6: "소호(6자리)까지", hs10: "국내 10자리까지" }[review.scope]} — {review.decidable || "-"}</div>
                <div><b>추가 확인사항</b>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{(review.questions || []).map((q, i) => <li key={i}>{q}</li>)}</ul>
                </div>
                <div style={{ color: MUTED }}>시스템 검토 의견(확정 아님): {review.opinion?.hsk ? `${formatHsk(review.opinion.hsk)} — ${review.opinion.why}` : "어느 쪽이라고 말할 근거가 부족합니다"}</div>
              </div>
            )}
            <div><button type="button" onClick={copySummary} style={btn(false)}>{copied ? "복사했습니다" : "검토 요약 복사"}</button>
              <span style={{ color: MUTED, fontSize: 12, marginLeft: 8 }}>검증 화면의 처리 이력에 붙여 넣어 기록하세요 · 이 화면은 검증 상태를 바꾸지 않습니다</span></div>
          </section>
        </>
      )}
    </div>
  );
}

function Row({ label, head, cell, codes, render }) {
  return (
    <tr>
      <th style={{ ...head, textAlign: "left" }}>{label}</th>
      {codes.map((h) => <td key={h} style={cell}>{render(h)}</td>)}
    </tr>
  );
}

function ConditionList({ cond, kind }) {
  if (!cond) return <span style={{ color: MUTED }}>…</span>;
  if (cond.supported === false) return <span style={{ color: MUTED }}>이 세번의 요건 자료가 아직 없습니다 — 세번 상세의 주·해설을 보세요</span>;
  const list = (cond[kind] || []).filter((x) => x && (x.text || x.condition || x.label));
  if (!list.length) return <span style={{ color: MUTED }}>없음</span>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {list.slice(0, 8).map((x, i) => (
        <li key={i}>{x.text || x.condition || x.label}{x.to ? ` → ${x.to}` : ""}{x.level ? <span style={{ color: MUTED }}> ({x.level})</span> : null}</li>
      ))}
    </ul>
  );
}

const linkBtn = { background: "none", border: 0, color: BLUE, cursor: "pointer", fontSize: 12 };
function inputStyle(w) { return { padding: "6px 8px", border: `1px solid ${LINE}`, borderRadius: 6, fontSize: 13, width: w, boxSizing: "border-box" }; }
function btn(primary) {
  return { padding: "6px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer", border: primary ? 0 : `1px solid ${LINE}`,
    background: primary ? BLUE : "#fff", color: primary ? "#fff" : "#1f2937" };
}
